import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, ListChecks } from "lucide-react";
import { MarkdownRender, ArtifactIframe, LinkArtifactView } from "@/pages/ModuleEditor";
import { sanitizeProseHtml } from "@/lib/sanitize";
import { PreviewBanner, ShareErrorScreen, ShareLoadingScreen } from "@/pages/PublicCoursePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public single-module preview. Read-only: artifact renders normally, the quiz
 *  is shown below as a static review list (correct answer pre-highlighted in red). */
export default function PublicModulePreview() {
  const { token, moduleId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/c/${token}/m/${moduleId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This module is not available."));
  }, [token, moduleId]);

  if (err) return <ShareErrorScreen message={err} />;
  if (!data) return <ShareLoadingScreen />;
  const { module: mod, course_title } = data;
  const isIframe = mod.artifact_type === "embed" || mod.artifact_type === "jsx";
  const isLink = mod.artifact_type === "link";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PreviewBanner />
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3 shrink-0">
        <button onClick={() => nav(`/share/c/${token}`)} className="text-slate-500 hover:text-[#E53935] transition-colors" data-testid="public-back-button">
          <ArrowLeft size={18}/>
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{course_title}</div>
          <div className="text-sm font-display font-bold text-slate-900 truncate">{mod.title}</div>
        </div>
      </header>

      {/* Artifact */}
      <div className="flex-1 min-h-0">
        {isLink ? (
          <div className="h-full min-h-[70vh] max-w-5xl mx-auto px-6 py-6">
            <LinkArtifactView url={mod.artifact_content || ""} />
          </div>
        ) : isIframe ? (
          <div className="h-[80vh]">
            <ArtifactIframe html={mod.artifact_content} mode={mod.artifact_type} viewport="desktop" moduleId={mod.id} learner={null}/>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
            {mod.artifact_type === "html"
              ? <div className="artifact-prose" dangerouslySetInnerHTML={{ __html: sanitizeProseHtml(mod.artifact_content) }} />
              : <MarkdownRender source={mod.artifact_content || ""} />}
          </div>
        )}
      </div>

      {/* Read-only quiz review */}
      {mod.quiz && mod.quiz.length > 0 && (
        <div className="bg-white border-t-2 border-[#E53935]/20 px-6 py-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-wider text-[#E53935] font-bold">
              <ListChecks size={13}/> Quiz preview · read-only
            </div>
            <h2 className="text-2xl font-display font-bold text-slate-900 mb-1">{mod.quiz.length} question{mod.quiz.length === 1 ? "" : "s"}</h2>
            <p className="text-xs text-slate-500 mb-8">Pass mark: {mod.pass_threshold || 70}% · Correct answer highlighted in red.</p>
            <ol className="space-y-6">
              {mod.quiz.map((q, qi) => (
                <li key={qi} className="border-l-4 border-slate-200 pl-5" data-testid={`public-quiz-q-${qi}`}>
                  <div className="text-xs text-slate-400 font-mono mb-1">Q{qi + 1}</div>
                  <div className="font-medium text-slate-900 mb-3">{q.q}</div>
                  <ul className="space-y-1.5 text-sm">
                    {(q.options || []).map((opt, oi) => {
                      const isCorrect = oi === q.answer;
                      return (
                        <li key={oi} className={`flex items-start gap-2 px-3 py-1.5 rounded ${isCorrect ? "bg-[#E53935]/5 border border-[#E53935]/40" : "bg-slate-50"}`}>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isCorrect ? "bg-[#E53935] text-white" : "bg-white border border-slate-300 text-slate-500"}`}>
                            {isCorrect ? <CheckCircle2 size={11}/> : String.fromCharCode(65 + oi)}
                          </span>
                          <span className={isCorrect ? "text-slate-900 font-medium" : "text-slate-600"}>{opt}</span>
                        </li>
                      );
                    })}
                  </ul>
                  {q.explanation && (
                    <div className="mt-3 text-xs text-slate-500 leading-relaxed italic">{q.explanation}</div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
